<template>
  <div ref="rootRef">
    <DSFieldLabel
      v-if="label"
      :id="labelId"
      :label
      class="mb-1.5"
    />
    <DSDateField
      :model-value="modelValue ?? undefined"
      :data-refid
      :aria-labelledby="label ? labelId : undefined"
      :aria-label="label ? undefined : 'Date'"
      :disabled
      :readonly
      :size="toDSInputSize(size)"
      @update:model-value="modelValue = $event ?? null"
      @keydown.enter="emit('click:enter', $event)"
      @keydown.tab="emit('click:tab', $event)"
      @keydown.esc="emit('click:escape', $event)"
    />
    <p
      v-if="label && error"
      class="mt-1.5 text-xs font-medium"
      :class="silent ? 'text-muted-foreground' : 'text-destructive'"
    >
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref, useId, useTemplateRef } from 'vue'

import { InputSize, TransactionClass } from '@types'

import { TRANSACTION_FIELD } from '../utils/const'

import { toDSInputSize } from '@/components/UI/Input/utils/helpers'

import { DSDateField, DSFieldLabel } from '@ds'

type Props = {
  dataRefid?: string
  disabled?: boolean
  readonly?: boolean
  instance: TransactionClass
  label?: string
  size?: InputSize
}

type Emits = {
  ready: [el: HTMLElement]
  'click:enter': [event: KeyboardEvent]
  'click:tab': [event: KeyboardEvent]
  'click:escape': [event: KeyboardEvent]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const rootRef = useTemplateRef('rootRef')

const labelId = useId()

const modelValue = props.instance.field<string | null>(TRANSACTION_FIELD.DATE)

const isSavedTransaction = inject('isSavedTransaction', ref(false))

const error = computed(() => props.instance.errors.date)
const silent = computed(() => !isSavedTransaction.value)

onMounted(() => {
  const input = rootRef.value?.querySelector('input')
  if (input) {
    // emits ready event with the input element to allow parent components to focus it if needed
    emit('ready', input)
  }
})
</script>
